import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col } from 'antd';
import UserCard from '../Organism/UserCard';
import axios from 'axios';
import { loginRequest } from '../../authConfig'; // Adjust the path to your authConfig file
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { AwardsName } from '../Atoms/AwardsName';
import { useNavigate } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import EligibilityModal from '../Molecules/EligibilityModal';
import TutorialGuide from '../Molecules/TutorialGuide';
import { Button, Switch } from 'antd';
import { IdcardOutlined, BarsOutlined, QuestionCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { Select } from 'antd';

interface User {
    nomineeName: string;
    teamName: string;
    title: string;
    department: string;
    badgeNumber: number;
    responses: {
        support: number;
        reject: number;
        noVisibility: number;
    };
    avatarInitials: string;
}

interface AwardData {
    stage: any;
}

const UserGrid: React.FC<AwardData> = ({ stage }) => {
    const { instance, inProgress, accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const baseURL = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();
    const startTourRef = React.useRef<(() => void) | null>(null);

    const [headerText, setHeaderText] = useState<string>('');
    const [dataFetched, setDataFetched] = useState(false);
    const [users, setUsers] = useState<User[]>([]);
    const [userMail, setUserMail] = useState('');
    const [eltKey, setEltKey] = useState<string | null>(null);
    const [superAdminKey, setSuperAdminKey] = useState<number | null>(null);
    const [isCriteriaVisible, setIsCriteriaVisible] = useState(false);
    const [selectedAward, setSelectedAward] = useState('');
    const [viewType, setViewType] = useState<'card' | 'table'>(stage === '7' || stage === '9' ? 'table' : 'card');
    const [currentPage, setCurrentPage] = useState(1); // If you use pagination
    const [selectedProduct, setSelectedProduct] = useState<string>('All');
    const [productNames, setProductNames] = useState<string[]>([]);
    const [selectedUnit, setSelectedUnit] = useState<string>('All');
    const [unitNames, setUnitNames] = useState<string[]>([]);

    useEffect(() => {
        fetchUserData()
    }, []);
    const openModal = (awardName: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setSelectedAward(awardName);
        setIsCriteriaVisible(true);
    };
    useEffect(() => {
        const productSet = new Set<string>();
        const unitSet = new Set<string>();
        if (stage === '8' || stage === '9') {
            // Team awards: filter by nominatorProductName
            users.forEach((user: any) => {
                if (user.nominatorProductName) productSet.add(user.nominatorProductName);
                if (user.nomineeUnit) unitSet.add(user.nomineeUnit);
            });
        } else {
            // Individual awards: filter by nomineeProductName
            users.forEach((user: any) => {
                if (user.nomineeProductName) productSet.add(user.nomineeProductName);
                if (user.nomineeUnit) unitSet.add(user.nomineeUnit);
            });
        }
        setProductNames(Array.from(productSet));
        setUnitNames(Array.from(unitSet));
    }, [users, stage]);

    const closeModal = () => {
        setIsCriteriaVisible(false);
    };

    const callMsGraph = async (token: any) => {
        const headers = new Headers();
        headers.append("Authorization", `Bearer ${token}`);
        const response = await fetch(`https://graph.microsoft.com/v1.0/me?`, {
            method: "GET",
            headers,
        }).then((response) => response.json());
        return response;
    };

    const requestProfileData = useCallback(async () => {
        try {
            const response = await instance.acquireTokenSilent({
                scopes: ["User.Read", "User.Read.All"],
                authority: `https://login.microsoftonline.com/560ec2b0-df0c-4e8c-9848-a15718863bb6`,
            });
            const data = await callMsGraph(response.accessToken);
            localStorage.setItem("UserMail", data?.mail.toLowerCase());
            setUserMail(data?.mail?.toLowerCase());
        } catch (error) {
            console.error("Silent token acquisition failed, acquiring token using redirect");
            instance.loginRedirect(loginRequest);
        }
    }, [instance]);

    const conditionCheck = useCallback(async () => {
        if (userMail !== '') {
            try {
                const res = await instance.acquireTokenSilent({
                    ...loginRequest,
                    account: accounts[0],
                });
                if (stage === '6' || stage === '8') {
                    const elt_email_key = await axios.get(`${baseURL}/api/nominations/users/${userMail}/eltkey`, {
                        headers: {
                            Authorization: `Bearer ${res.idToken}`,
                        },
                    });
                    localStorage.setItem('eltEmailKey', elt_email_key.data.data['eltEmailKey']);
                    setEltKey(elt_email_key.data.data['eltEmailKey']);
                }
                if (stage === '7' || stage === '9') {
                    const super_admin_key = await axios.get(`${baseURL}/api/nominations/users/${userMail}/admin`, {
                        headers: {
                            Authorization: `Bearer ${res.idToken}`,
                        },
                    });
                    if (super_admin_key.data !== 5) {
                        navigate('/no-access');
                    }
                    localStorage.setItem('superAdminKey', super_admin_key.data);
                    setSuperAdminKey(super_admin_key.data);
                }
            } catch (error: any) {
                if (axios.isAxiosError(error) && error.response?.status === 404) {
                    navigate('/no-access');
                } else {
                    console.error("An error occurred:", error);
                    navigate('/technical-error');
                }
            }
        }
    }, [instance, userMail, accounts, baseURL, navigate]);
    const handleViewTypeChange = async (type: 'card' | 'table') => {
        setViewType(type);
        setCurrentPage(1); // Reset to first page if paginated
        await fetchUserData(); // Refetch data
    };
    useEffect(() => {
        const fetchData = async () => {
            try {
                await requestProfileData();
                await conditionCheck();
            } catch (error) {
                navigate('/technical-error');
            }
        };
        if (inProgress === InteractionStatus.None) {
            if (!isAuthenticated) {
                instance.loginRedirect({ scopes: ["User.Read", "User.Read.All"] });
            } else {
                fetchData();
            }
        }
    }, [instance, isAuthenticated, navigate, inProgress, requestProfileData, userMail, conditionCheck]);

    const fetchUserData = async () => {
        try {
            const response = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0]
            });
            const res = await axios.get(`${baseURL}/api/nominations`, {
                headers: {
                    Authorization: `Bearer ${response.idToken}`
                }
            });
            const getUsers = async () => {
                const data = res.data.responseData;
                let sortedData = data;
                if (stage === '7' || stage === '9') {
                    data.forEach((item: any) => {
                        const eltScores = [
                            item.elt1Score, item.elt2Score, item.elt3Score, item.elt4Score,
                            item.elt5Score, item.elt6Score, item.elt7Score, item.elt8Score,
                            item.elt9Score, item.elt10Score, item.elt11Score, item.elt12Score,
                            item.elt13Score, item.elt14Score, item.elt15Score, item.elt16Score,
                            item.elt17Score
                        ].filter(score => score !== null);

                        item.eltAverageScore = eltScores.length > 0
                            ? eltScores.reduce((acc, score) => acc + parseFloat(score), 0) / eltScores.length
                            : 0;
                    });

                    sortedData = data.sort((a: any, b: any) => b.eltAverageScore - a.eltAverageScore);
                }
                if (stage === '6' || stage === '7') {

                    setHeaderText('Individual Award Nominees');
                    // if (stage === '6') {
                    //     const filteredData = data.filter((item: any) => item.rewardName === AwardsName.IndividualAwards);
                    //     const reviewData = filteredData.filter((item: any) => item.secondLevelApproverVote === 'Approved');
                    //     setUsers(reviewData);

                    // }
                    // if (stage === '7') {
                    //     const filteredData = sortedData.filter((item: any) => item.rewardName === AwardsName.IndividualAwards);
                    //     const reviewData = filteredData.filter((item: any) => item.secondLevelApproverVote === 'Approved');
                    //     setUsers(reviewData);
                    // }
                    if (stage === '6') {
                        const filteredData = data.filter((item: any) => item.rewardName === AwardsName.IndividualAwards);
                        const reviewData = filteredData.filter((item: any) => item.secondLevelApproverVote === 'Approved');
                        const mergedData = mergeNominations(reviewData);
                        setUsers(mergedData);
                    }
                    if (stage === '7') {
                        const filteredData = sortedData.filter((item: any) => item.rewardName === AwardsName.IndividualAwards);
                        const reviewData = filteredData.filter((item: any) => item.secondLevelApproverVote === 'Approved');
                        const mergedData = mergeNominations(reviewData);
                        setUsers(mergedData);
                    }
                } else if (stage === '8' || stage === '9') {
                    setHeaderText('Team Award Nominees');
                    if (stage === '8') {
                        const filteredData = data.filter((item: any) => item.rewardName === AwardsName.TeamAwards);
                        const reviewData = filteredData.filter((item: any) => item.secondLevelApproverVote === 'Approved');
                        setUsers(reviewData);
                    }
                    if (stage === '9') {
                        const filteredData = sortedData.filter((item: any) => item.rewardName === AwardsName.TeamAwards);
                        const reviewData = filteredData.filter((item: any) => item.secondLevelApproverVote === 'Approved');
                        setUsers(reviewData);
                    }
                } else {
                    setUsers(data);
                }
                setDataFetched(true);
            };
            getUsers();
            return res.data.responseData;
        } catch (silentError) {
            if (silentError instanceof InteractionRequiredAuthError) {
                try {
                    const response = await instance.acquireTokenPopup({
                        ...loginRequest,
                        account: accounts[0]
                    });
                    const res = await axios.get(`${baseURL}/api/nominations`, {
                        headers: {
                            Authorization: `Bearer ${response.idToken}`
                        }
                    });
                    return res.data.responseData;
                } catch (popupError) {
                    console.error(popupError);
                    return [];
                }
            } else {
                console.error(silentError);
                return [];
            }
        }
    };
    const filteredUsers = users.filter((user: any) => {
        let productMatch = true;
        if (stage === '8' || stage === '9') {
            // Team awards: filter by nominatorProductName
            productMatch = selectedProduct === 'All' || user.nominatorProductName === selectedProduct;
        } else {
            // Individual awards: filter by nomineeProductName
            productMatch = selectedProduct === 'All' || user.nomineeProductName === selectedProduct;
        }
        const unitMatch = selectedUnit === 'All' || user.nomineeUnit === selectedUnit;
        return productMatch && unitMatch;
    });

    const gradeOrder = (grade: string) => {
        if (!grade) return -1;
        return grade.toUpperCase().charCodeAt(0) - 65; // 'A' is 0, 'B' is 1, etc.
    };

    function mergeNominations(nominations: any[]) {
        const merged: { [email: string]: any } = {};

        nominations.forEach(nom => {
            const email = nom.nomineeEmail;
            if (!merged[email]) {
                // Start with a copy of the first nomination
                merged[email] = { ...nom };
                // Store nominators as array of { name, formFields }
                merged[email].allNominators = [{
                    nominatorName: nom.nominatorName,
                    nominatorGrade: nom.nominatorGrade,
                    formFields: nom.formFields
                }];
            } else {
                // Add this nominator's info
                merged[email].allNominators.push({
                    nominatorName: nom.nominatorName,
                    nominatorGrade: nom.nominatorGrade,
                    formFields: nom.formFields
                });
                // If this nomination has a higher nominatorGrade, replace main fields
                const currentGrade = merged[email].nominatorGrade;
                if (gradeOrder(nom.nominatorGrade) > gradeOrder(currentGrade)) {
                    Object.assign(merged[email], nom);
                }
            }
        });

        // Format allNominators into a single string for display
        return Object.values(merged).map((nom: any) => {
            nom.nominationCount = nom.allNominators.length; // <-- Add this line
            nom.formFields = nom.allNominators.map(
                (n: { nominatorName: string; nominatorGrade: string; formFields: string }) =>
                    `Nominator: ${n.nominatorName}\n${n.formFields}`
            ).join('\n\n---\n\n');
            delete nom.allNominators;
            return nom;
        });
    }
    return (
        <>
            <TutorialGuide viewType={viewType} stage={stage} externalStartTourRef={startTourRef} />
            <style>
                {`
                .usergrid-header-bar {
                    position: sticky;
                    top: 0;
                    z-index: 10;
                    background: #fff;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    padding: 0.7rem 1.2rem 0.7rem 1.2rem;
                    margin-bottom: 1.2rem;
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    justify-content: space-between;
                    gap: 1.2rem;
                }
                .usergrid-header-title {
                    font-size: 1.18rem;
                    font-weight: 700;
                    color: #222;
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                    margin-right: 1.2rem;
                }
                .usergrid-header-filters {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    min-width: 320px;
                }
                .usergrid-header-actions {
                    display: flex;
                    align-items: center;
                    gap: 0.7rem;
                    margin-left: auto;
                }
                .usergrid-help-button {
                    margin-left: 1rem;
                    padding-right: 0.5rem;
                }
                .usergrid-filter {
                    min-width: 140px;
                }
                .usergrid-content {
                    padding: 0 2rem 2rem 2rem;
                }
                @media (max-width: 900px) {
                    .usergrid-header-bar, .usergrid-content {
                        padding: 0.5rem 0.5rem 1rem 0.5rem;
                    }
                }
                @media (max-width: 600px) {
                    .usergrid-header-bar {
                        flex-direction: column;
                        align-items: stretch;
                        gap: 0.5rem;
                    }
                    .usergrid-header-title {
                        margin-right: 0;
                    }
                    .usergrid-header-actions {
                        margin-left: 0;
                    }
                }
                `}
            </style>
            <div className="usergrid-header-bar">
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, gap: '1.5rem' }}>
                    <div className="usergrid-header-title">
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {headerText}
                            <button
                                onClick={openModal("ACE Awards")}
                                style={{
                                    fontSize: '0.92rem',
                                    fontWeight: 500,
                                    color: '#1677ff',
                                    marginLeft: 7,
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    cursor: 'pointer',
                                    outline: 'none',
                                    textDecoration: 'underline',
                                    lineHeight: 1.2
                                }}
                                tabIndex={0}
                                aria-label="View criteria"
                            >
                                (View criteria)
                            </button>
                        </div>
                    </div>
                    {(headerText === 'Individual Award Nominees' || headerText === 'Team Award Nominees') && (
                        <div className="usergrid-header-filters">
                            <Select
                                className="usergrid-filter"
                                value={selectedProduct}
                                onChange={setSelectedProduct}
                                showSearch
                                optionFilterProp="children"
                                placeholder={stage === '8' || stage === '9' ? 'Filter by Nominator Product' : 'Filter by Product Name'}
                                style={{ width: 140 }}
                            >
                                <Select.Option value="All">All Products</Select.Option>
                                {productNames.map(product => (
                                    <Select.Option key={product} value={product}>{product}</Select.Option>
                                ))}
                            </Select>
                            {/* Only show Business Unit filter for individual awards */}
                            {(stage !== '8' && stage !== '9') && (
                                <Select
                                    className="usergrid-filter"
                                    value={selectedUnit}
                                    onChange={setSelectedUnit}
                                    showSearch
                                    optionFilterProp="children"
                                    placeholder="Filter by Business Unit"
                                    style={{ width: 140 }}
                                >
                                    <Select.Option value="All">All Business Units</Select.Option>
                                    {unitNames.map(unit => (
                                        <Select.Option key={unit} value={unit}>{unit}</Select.Option>
                                    ))}
                                </Select>
                            )}
                        </div>
                    )}
                </div>
                <div className="usergrid-header-actions">
                    <Tooltip title="Card View">
                        <Button
                            type={viewType === 'card' ? 'primary' : 'default'}
                            onClick={() => handleViewTypeChange('card')}
                            icon={<IdcardOutlined style={{ fontSize: 22, color: viewType === 'card' ? '#1677ff' : '#bfbfbf' }} />}
                            style={{ width: 40, height: 40, borderRadius: 10, boxShadow: viewType === 'card' ? '0 2px 8px #e6f7ff' : undefined, background: viewType === 'card' ? '#e6f7ff' : undefined, border: viewType === 'card' ? '1.5px solid #1677ff' : '1.5px solid #f0f0f0' }}
                        />
                    </Tooltip>
                    <Tooltip title="Table View">
                        <Button
                            type={viewType === 'table' ? 'primary' : 'default'}
                            onClick={() => handleViewTypeChange('table')}
                            icon={<BarsOutlined style={{ fontSize: 22, color: viewType === 'table' ? '#52c41a' : '#bfbfbf' }} />}
                            style={{ width: 40, height: 40, borderRadius: 10, boxShadow: viewType === 'table' ? '0 2px 8px #f6ffed' : undefined, background: viewType === 'table' ? '#f6ffed' : undefined, border: viewType === 'table' ? '1.5px solid #52c41a' : '1.5px solid #f0f0f0', marginLeft: 6 }}
                        />
                    </Tooltip>
                    <div className="usergrid-help-button">
                        <Button
                            type="text"
                            icon={<QuestionCircleOutlined />}
                            onClick={() => startTourRef.current && startTourRef.current()}
                            style={{
                                fontSize: '14px',
                                color: '#1677ff',
                                padding: '4px 8px',
                            }}
                        >
                            Help
                        </Button>
                    </div>
                </div>
            </div>
            {isCriteriaVisible && (
                <EligibilityModal
                    visible={isCriteriaVisible}
                    onClose={closeModal}
                    awardName={selectedAward}
                />
            )}
            <div className="usergrid-content">
                {(eltKey !== null || superAdminKey !== null) ? (
                    <>
                        {viewType === 'card' ? (
                            <Row gutter={[24, 24]}>
                                {filteredUsers.map(user => (
                                    <Col key={user.nomineeName} xs={24} sm={12} md={8} lg={8}>
                                        <UserCard {...user} stage={stage} empdata={[user]} />
                                    </Col>
                                ))}
                            </Row>
                        ) : (
                            <UserCard stage={stage} empdata={filteredUsers} tabular />
                        )}
                    </>
                ) : (
                    <div style={{ textAlign: 'center', padding: '3rem 0', color: '#888', fontSize: '1.2rem' }}>Loading nominees...</div>
                )}
            </div>
        </>
    );
};

export default UserGrid;
